import Element, { ElementType, SpecyficType } from './element';
import { Vector3, Vector2 } from '@babylonjs/core';
import { HelpPoint } from './helpPoint';
import { Hinge } from './hinge';
import store from '@/store';
import { ProductType } from '@/store/modules/product';
import { SlotElement } from './slotElement';

class PositionManager {
    private body!: Element;

    /**
     * Procedura ustawiająca główny element, który posłuży jako punkt odniesienia do obliczeń.
     * @param body Korpus projektowanego mebla.
     */
    public initialize(body: Element) {
        this.body = body;
    }

    /**
     * Procedura umieszczająca element w odpowiednim miejscu na scenie.
     * @param element Element, który należy umieścić w odpowiednim miejscu na scenie.
     */
    public position(element: Element) {
        if (element.isStatic) { return; }
        const position = this.getInitialPosition(element);
        this.correctPosition(element, position);
        element.center = position;
    }

    /**
     * Procedura umieszczająca element na odpowiednim miejscu na
     * scenie na podstawie danych zawartych w elemencie nadrzędnym.
     * @param element Element, który należy umieścić w odpowiednim miejscu na scenie.
     * @param parentPosition Pozycja elementu nadrzędnego dla elementu umieszczanego.
     * @param helpPoint Punkt wspomagający określający przesunięcie względem elementu nadrzędnego.
     */
    public positionWithParent(element: Element, parentPosition: Vector3, helpPoint: HelpPoint) {
        const helpPosition = helpPoint.node.position;
        element.center = parentPosition.add(helpPosition);
    }

    /**
     * Procedura umieszczająca zawias w odpowiednim miejscu.
     * @param element Zawias do spozycjonowania.
     * @param hinge Dodatkowe dane na temat zawiasu.
     */
    public positionHinge(element: Element, hinge: Hinge) {
        const position = this.getInitialPosition(element);
        position.x += (hinge.left ? 1 : -1) * this.cellWidth / 2;
        position.x += (hinge.left ? -1 : 1) * element.width;
        position.z += this.body.depth / 2 - element.depth / 2 - 0.5;
        position.y += (hinge.down ? -1 : 1) * (element.shape.y * this.cellHeight / 2 - 6.7);
        element.center = position;
    }

    public get cellWidth(): number {
        return this.body.width / this.body.shape.x;
    }

    public get cellHeight(): number {
        return this.body.height / this.body.shape.y;
    }

    public get cellDepth(): number {
        return this.body.depth;
    }

    private getInitialPosition(element: Element): Vector3 {
        const position = element.center;
        const avgX = (1 + this.body.shape.x) / 2;
        const avgY = (1 + this.body.shape.y) / 2;
        const offsetX = (element.position.x - avgX);
        const offsetY = (element.position.y - avgY);
        position.x = this.body.center.x - offsetX * this.cellWidth;
        position.y = this.body.center.y + offsetY * this.cellHeight;
        position.z = this.body.center.z;
        return position;
    }

    private correctPosition(element: Element, position: Vector3) {
        if (element.type === ElementType.FRONT) {
            const center = element.center;
            position.y = center.y;
            position.z = center.z;

            if (store.getters.product.type === ProductType.DRESSING) {
                position.z -= 0.5;
                position.y += 0.5;
            }
        }
        if (element.specyficType === SpecyficType.HORIZONTAL_WALL) {
            position.y -= element.height / 2 - 1.5;
        }
        if (element.type === ElementType.WALL) {
            position.z += 0.2;
            if (store.getters.product.type === ProductType.DESK) {
                position.z += 10.9;
            }
            if (store.getters.product.type === ProductType.DRESSING) {
                position.y += 6;
                position.z += 1;
            }
        }
        if (element.specyficType === SpecyficType.SHELF) {
            position.y -= this.cellHeight / 2;
            position.z += 0.2;
        }
        if (element.specyficType === SpecyficType.HANGER) {
            position.y += this.cellHeight * 2 / 3 + 2;
        }
        if (element.type === ElementType.WALL_SUPPORT) {
            position.y -= element.height;
        }
        if (element.specyficType === SpecyficType.DRAWER) {
            position.z += 0.1;
            if (store.getters.product.type !== ProductType.BEDSIDE_TABLE) {
                position.z += 2.8;
                position.y -= 1.0;
            }
            if (store.getters.product.type === ProductType.DESK) {
                position.z += 8.5;
                position.y += 1.0;
            }
            if (store.getters.product.type === ProductType.DRESSING) {
                position.y += 7;
                position.z -= 1.5;
            }
        }
        if (element.type === ElementType.SLOT) {
            const avgX = (1 + this.body.shape.x) / 2;
            const elementPosition = new Vector2(
                element.position.x - ((element.position.x - avgX) * 2),
                element.position.y,
            );
            const offsetX = (elementPosition.x - avgX);
            position.x = this.body.center.x - offsetX * this.cellWidth;
            position.x += (4 - this.body.shape.x) * this.cellWidth;

            position.z -= (55.85 - this.body.depth);

            if (element instanceof SlotElement &&
                element.slotSpecyficType === SpecyficType.HANGER) {
                position.y += (this.cellHeight * 2 / 3 + 2) / 2 - 3;
            }

            if (element instanceof SlotElement &&
                element.slotSpecyficType === SpecyficType.SHELF) {
                position.y -= 0.9;
            }

            if (store.getters.product.type === ProductType.DESK) {
                position.z += 5;
                position.x += 1.3;
            }

            if (store.getters.product.type === ProductType.DRESSING) {
                position.z += 5.5;
                position.y += 6;
                position.x += 0.7;
            }
        }
    }
}

export const positionManager = new PositionManager();

/* Oznaczenia poszczególnych komórek korpusu.
----------
|13|23|33|
----------
|12|22|32|
----------
|11|21|31|
----------
*/
